import { useCallback, useRef, useState } from 'react';

import { isNil } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IStore } from '@rbi-ctg/store';
import { useOrderDetails } from 'hooks/use-order-details';
import { useServiceModeStatus } from 'hooks/use-service-mode-status';
import { serviceModeList } from 'pages/cart/service-mode-details/service-mode-list';
import { NotFoundMenuItemError } from 'remote/exceptions';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLocationContext } from 'state/location';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { useRestaurantPosConnectivityStatus } from 'state/restaurant-connectivity-status';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import {
  checkIfWithinOneHourOfCloseToday,
  isMobileOrderingAvailable as getIsMobileOrderingAvailable,
  milesBetweenCoordinates,
  readableCloseHourToday,
  readableDistanceFromStore,
} from 'utils/restaurant';
import { routes } from 'utils/routing';
import { isCatering } from 'utils/service-mode';
import { getStoreSEOSlug } from 'utils/slugify';

export function useStoreCard(restaurant: IStore) {
  const {
    selectStore,
    reorder: { handleReorder, pendingReorder },
  } = useOrderContext();
  const { order } = useOrderDetails(pendingReorder?.rbiOrderId);
  const { activeCoordinates } = useGeolocation();
  const { isStoreOpenAndAvailable } = useStoreContext();
  const isStoreOpenAndAvailableRef = useRef(isStoreOpenAndAvailable);
  const handleReorderRef = useRef(handleReorder);
  handleReorderRef.current = handleReorder;
  isStoreOpenAndAvailableRef.current = isStoreOpenAndAvailable;
  const { serviceMode, setServiceMode } = useServiceModeContext();
  const { region } = useLocale();
  const { formatMessage } = useIntl();
  const { restaurantCanBeSelected, serviceModeStatus } = useServiceModeStatus(restaurant);
  const driveThruOpen =
    serviceModeStatus.DRIVE_THRU.available && !serviceModeStatus.DRIVE_THRU.disabled;
  const dineInOpen = serviceModeStatus.EAT_IN.available && !serviceModeStatus.EAT_IN.disabled;
  const takeoutOpen = serviceModeStatus.TAKEOUT.available && !serviceModeStatus.TAKEOUT.disabled;
  const diningRoomOpen = dineInOpen || takeoutOpen;
  const curbsideOpen = serviceModeStatus.CURBSIDE.available && !serviceModeStatus.CURBSIDE.disabled;
  const [checkingItemAvailability, setCheckingItemAvailability] = useState(false);
  const { logEvent } = useMParticleContext();
  const {
    checkStaticMenuItemAvailability,
    selectedStaticMenuItemId,
    showStaticMenu,
  } = useMenuContext();
  const enableServiceModeCartSelection = useFlag(
    LaunchDarklyFlag.ENABLE_SERVICE_MODE_CART_SELECTION
  );
  const defaultServiceModeInStore = useFlag(LaunchDarklyFlag.DEFAULT_SERVICE_MODE_IN_STORE);
  const openHours = driveThruOpen ? restaurant.driveThruHours : restaurant.diningRoomHours;
  const enableOrderingFlag = useFlag(LaunchDarklyFlag.ENABLE_ORDERING);
  const enableOrdering = isNil(enableOrderingFlag) || enableOrderingFlag;
  const areDiagnosticToolsEnabled = useFlag(LaunchDarklyFlag.ENABLE_INTERNAL_DIAGNOSTIC_TOOLS);
  const { storeLocatorCallbackUrl } = useLocationContext();
  const navigate = useNavigate();

  const closeHourTodayText = readableCloseHourToday(openHours);

  const isWithinOneHourOfCloseToday = checkIfWithinOneHourOfCloseToday(openHours);

  const { isRestaurantPosOnline } = useRestaurantPosConnectivityStatus(restaurant.number);

  let distanceText;
  if (activeCoordinates) {
    const distance = milesBetweenCoordinates(activeCoordinates, {
      lat: restaurant.latitude,
      lng: restaurant.longitude,
    });
    distanceText = readableDistanceFromStore(distance, region, formatMessage);
  }

  const selectRestaurant = useCallback(() => {
    selectStore(restaurant, async () => {
      if (!enableOrdering) {
        navigate(routes.offers);

        return;
      }

      if (isCatering(serviceMode)) {
        navigate(routes.menu);

        return;
      }

      if (enableServiceModeCartSelection) {
        logEvent(CustomEventNames.ORDER_HERE_STORE_CONFIRMATION_FLOW, EventTypes.Navigation, {
          'Restaurant Id': restaurant._id,
          'Restaurant Name': restaurant.name,
        });

        // get default service mode from ld
        const defaultAvailableServiceMode = serviceModeList.find(key => {
          return (
            key === defaultServiceModeInStore &&
            serviceModeStatus[key].available &&
            !serviceModeStatus[key].disabled
          );
        });
        // default service mode to:
        // 1) ld flag defaultServiceModeInStore
        // 2) the first available shown in cart when choosing a store
        const availableServiceMode =
          defaultAvailableServiceMode ||
          serviceModeList.find(key => {
            return serviceModeStatus[key].available && !serviceModeStatus[key].disabled;
          });

        if (availableServiceMode) {
          setServiceMode(availableServiceMode);
        }
      }
      // if user has a pending reorder redirect to the cart page
      if (isStoreOpenAndAvailableRef.current && order) {
        return handleReorderRef.current(order);
      }

      // If showing static menu and user has selected item redirect to the item if
      // available after store selection, otherwise route to main menu
      if (showStaticMenu && selectedStaticMenuItemId) {
        try {
          setCheckingItemAvailability(true);
          await checkStaticMenuItemAvailability(restaurant);
          return;
        } catch (e) {
          throw new NotFoundMenuItemError(selectedStaticMenuItemId);
        } finally {
          setCheckingItemAvailability(false);
        }
      } else if (enableServiceModeCartSelection) {
        navigate(storeLocatorCallbackUrl || routes.menu);
      } else {
        navigate(routes.pickupMode, { replace: true });
      }
    });
  }, [
    selectStore,
    restaurant,
    enableOrdering,
    serviceMode,
    enableServiceModeCartSelection,
    order,
    showStaticMenu,
    selectedStaticMenuItemId,
    navigate,
    logEvent,
    defaultServiceModeInStore,
    serviceModeStatus,
    setServiceMode,
    checkStaticMenuItemAvailability,
    storeLocatorCallbackUrl,
  ]);

  const isMobileOrderingAvailable = getIsMobileOrderingAvailable(restaurant);

  const storeSEOSlug = getStoreSEOSlug(restaurant);

  return {
    storeSEOSlug,
    distanceText,
    curbsideOpen,
    dineInOpen,
    takeoutOpen,
    diningRoomOpen,
    driveThruOpen,
    openHours,
    selectRestaurant,
    isRestaurantOpen: isCatering(serviceMode) || restaurantCanBeSelected,
    isMobileOrderingAvailable,
    isWithinOneHourOfCloseToday,
    closeHourTodayText,
    isRestaurantPosOnline: !!isRestaurantPosOnline,
    enableOrdering,
    areDiagnosticToolsEnabled,
    checkingItemAvailability,
  };
}
